import appConfig from 'appConfig';
import featureKeys from './featureKeys';
import { createHarness, defaultHarnessKeyFromLegacyKey } from '@pearsontechnology/bronte-lib-harness';

/**
 * Runtime feature flags via Harness FME (Split `frontend` flag-set + OpenFeature).
 * The in-memory map (`featureFlags`) uses Harness hyphen keys from Split; CDP/plugins
 * keep stable dotted names in `featureKeys.js` — resolve with `defaultHarnessKeyFromLegacyKey`
 * (dots → hyphens; `_`/`-` unchanged).
 */
export default (() => {
  let featureFlags = {};
  let _subscriptions = [];
  let isInitialized = false;
  let isInitializedPromiseResolve;
  let userIdFallBack = {accountId:'anonymous'};

  let isInitializedPromise = new Promise((resolve) => {
    isInitializedPromiseResolve = resolve;
  });

  /**
   * @param {string} featureFlagThatChanged
   * @param {boolean} [effectiveValue] When set (e.g. from harness reapply), updates `featureFlags` before callbacks run.
   */
  function notifyListeners(featureFlagThatChanged, effectiveValue) {
    if (typeof effectiveValue === 'boolean') {
      featureFlags[featureFlagThatChanged] = effectiveValue;
    }
    _subscriptions.forEach((item) => {
      const hyphenSubMatchesDottedNotify =
        item.flag === defaultHarnessKeyFromLegacyKey(featureFlagThatChanged);
      const sameCanonicalFlag =
        defaultHarnessKeyFromLegacyKey(item.flag) ===
        defaultHarnessKeyFromLegacyKey(featureFlagThatChanged);
      if (
        item.flag === featureFlagThatChanged ||
        hyphenSubMatchesDottedNotify ||
        sameCanonicalFlag
      ) {
        try {
          const result = item.callback(
            Object.freeze({
              flagName: featureFlagThatChanged,
              value:
                featureFlags[featureFlagThatChanged] ??
                featureFlags[defaultHarnessKeyFromLegacyKey(featureFlagThatChanged)],
            })
          );
          Promise.resolve(result).catch(() => unsubscribe(item.flag));
        } catch (e) {
          unsubscribe(item.flag);
        }
      }
    });
  }

  function unsubscribe(flag) {
    _subscriptions = _subscriptions.filter((sub) => sub.flag !== flag);
  }

  function warnFeatureFlagResolution(reason, featureName, detail) {
    const suffix = detail ? ` ${detail}` : '';
    /*eslint-disable-next-line no-console*/
    console.warn(`[FeatureFlags] ${reason} (featureName=${featureName})${suffix}`);
    return false;
  }

  /** Lazy so importing this module does not read `appConfig` (tests often use partial appConfig mocks). */
  let harnessSingleton = null;
  function getHarness() {
    if (!harnessSingleton) {
      harnessSingleton = createHarness({
        authorizationKey: appConfig.getHarnessFMESDKKey(),
        notifyListeners,
      });
    }
    return harnessSingleton;
  }

  async function initializeFeatureFlags(user) {
    await getHarness().teardownHarness();
    user = user?.accountId ? user: userIdFallBack;
    featureFlags = await getHarness().setupHarness(user);
    getHarness().registerConfigurationListenerAfterMerge(featureFlags);

    if (!isInitialized) {
      isInitializedPromiseResolve();
      isInitialized = true;
    }

    _subscriptions.forEach((item) => {
      notifyListeners(item.flag);
    });

    return Object.freeze({...featureFlags});
  }

  function get() {
    if (!isInitialized) {
      throw new Error(
        "You tried to get the feature flags before the feature flag service got initialized"
      );
    }
    return Object.freeze({ ...featureFlags });
  }

  async function getWhenReady() {
    await isInitializedPromise;
    return Object.freeze({ ...featureFlags });
  }

  /**
   * @deprecated for React Components.
   * Use getFeatureFlagValue via 'hocs/enhanceWithFeatureFlagSubscription'.
   */
  function is(featureName) {
    if (!isInitialized) {
      throw new Error(`You tried to check the status of ${featureName} before the service got initialized`);
    }
    if (featureFlags.hasOwnProperty(featureName)) {
      return featureFlags[featureName];
    }
    const harnessKey = defaultHarnessKeyFromLegacyKey(featureName);
    if (featureFlags.hasOwnProperty(harnessKey)) {
      return featureFlags[harnessKey];
    }
    const harnessResult = getHarness().getHarnessBooleanForLegacyKey(featureName);
    if (harnessResult === null) {
      return warnFeatureFlagResolution(
        'harness_not_ready',
        featureName,
        'Harness is not available to evaluate; try again after initialization completes'
      );
    }
    return harnessResult;
  }

  function set(data) {
    const { name, isSet } = data;
    featureFlags[name] = isSet;
    notifyListeners(name);
    return featureFlags[name];
  }

  function subscribe(featureFlag, callbackFn) {
    const newSubscription = {
      flag: featureFlag,
      callback: callbackFn,
    };

    _subscriptions.push(newSubscription);

    isInitializedPromise.then(() => {
      getHarness().reapplyMappedKeysFromSplit(true, featureFlags);
      notifyListeners(featureFlag);
    });

    return ((flag) => unsubscribe(flag));
  }

  return {
    initializeFeatureFlags,
    subscribe,
    get,
    getWhenReady,
    is,
    set,
    unsubscribe,
  };
})();

export { featureKeys };
